import { useCallback, useEffect, useMemo, useState } from 'react'
import { ButtonOrLoader } from '../../../../../../../../baseComponents/Button'
import { ModalMedium } from '../../../../../../../../baseComponents/Modal'
import { ModalComponentPropsWithoutSubmit } from '../../../../../../../../baseComponents/ModalsLayouts/types'
import { twinFetchPostJSON } from '../../../../../../../../utils/globals/data'
import useIsLoading from '../../../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { getTableSelectedColumns, SelectedColumnsAndFilters } from '../../../../../../../../utils/hooks/useConfigColumns'
import { changeEmployeeParams } from '../../../../../../../../utils/reducers/reduxDispatch'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import { VerticalSortItem } from '../../../../../../../../baseComponents/Sorts/VerticalSortList/types'
import { VerticalSortListDelete } from '../../../../../../../../baseComponents/Sorts/VerticalSortList'
import { ButtonLoadFiltersStateLess, FiltersConfig, useButtonsLoadFiltersStateFullLogic } from '../../../../../../../../baseComponents/Button/ButtonLoadFilter'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { CustomChangeFunction } from '../../../../../../../../forms/Input/types'
import { CustomSelectWithSearchBarNotInFilteredItems } from '../../../../../../../../forms/CustomSelect'
import { getEmployees } from '../../../../../../../../utils/reducers/getters'
import { TypeSellTableNameTy } from '../types'

type ModalConfigStatsByEmployeeProps = ModalConfigStatsByEmployeeLogicProps & {
    selectedColumnsAndFilters: SelectedColumnsAndFilters
}

const ModalConfigStatsByEmployee: React.FC<ModalConfigStatsByEmployeeProps> = ({ setOpened, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeTableName }) => {
    const { loading, changeData, onSubmitHandler, mySelectedEmployees, copySelectedColumns, updateEmployeeConfig, isCustomFilter, myOnChangeFilter } = useModalConfigStatsByEmployeeLogic({ setOpened, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeTableName })
    const { t } = useTwinTranslation()
    const name = isCustomFilter && copySelectedColumns?.name ? copySelectedColumns?.name : t('byDefault', 'Por defecto')
    const mySelectedFilter: FiltersConfig = { value: copySelectedColumns?.filters?.EmployeeId?.value, id: copySelectedColumns?.id, name: copySelectedColumns?.name }
    const defaultValues = getTableSelectedColumns('employee_' + typeTableName + '_config_stats', [])
    const { filters, getAllFilters } = useButtonsLoadFiltersStateFullLogic({ tableName: 'table_employee_' + typeTableName +'_config_stats', defaultValues })
    return (
        <ModalMedium opened={true} setOpened={setOpened} className='flex flex-col '>
            <div className='flex items-center flex-wrap gap-10 mb-20'>
                <span className='medium16'><TwinTrans transKey='configuration'>Configuración</TwinTrans>: {name}</span>
                <ButtonLoadFiltersStateLess selectedFilters={mySelectedFilter} onChangeFilter={myOnChangeFilter} tableName={'table_employee_' + typeTableName + '_config_stats'} allowCreate={true} defaultValues={defaultValues} filters={filters} getAllFilters={getAllFilters} onOpenModal={getAllFilters} />
            </div>
            <SearchBarEmployee className='mb-5' changeSelectedItems={changeData} excludeIds={copySelectedColumns.filters?.EmployeeId?.value || []} label={t('searchEmployeeDots', 'Buscar empleado...')} />
            <div className='flex-auto table_container mt-20'>
                <VerticalSortListDelete items={mySelectedEmployees || []} setItems={(value) => {updateEmployeeConfig(value(mySelectedEmployees))}} />
            </div>
            <div className='flex justify-end'>
                <ButtonOrLoader onClick={onSubmitHandler} buttonIsDisabled={loading} textButton={t('save', 'Guardar')} className='mt-68' />
            </div>
        </ModalMedium>
    )
}

interface ModalConfigStatsByEmployeeLogicProps extends ModalComponentPropsWithoutSubmit {
    typeTableName: TypeSellTableNameTy
    selectedColumnsAndFilters: SelectedColumnsAndFilters
    setSelectedColumnsAndFilters: React.Dispatch<React.SetStateAction<SelectedColumnsAndFilters>>
}

const useModalConfigStatsByEmployeeLogic = ({ setOpened, selectedColumnsAndFilters, setSelectedColumnsAndFilters, typeTableName }: ModalConfigStatsByEmployeeLogicProps) => {
    const { loading, startLoading } = useIsLoading()
    const [copySelectedColumns, setCopySelectedColumns] = useState<SelectedColumnsAndFilters>(JSON.parse(JSON.stringify(selectedColumnsAndFilters)))
    const [mySelectedEmployees, setMySelectedEmployees] = useState<VerticalSortItem[] | null>(null)
    const isCustomFilter = copySelectedColumns?.id && String(copySelectedColumns?.id) !== '0'
    const name = 'employee_' + typeTableName + '_config_stats'
    const mySelectedIds = useMemo(() => copySelectedColumns?.filters?.EmployeeId?.value || [], [copySelectedColumns?.filters?.EmployeeId?.value])
    const myEmployeesOrder = useMemo(() => copySelectedColumns?.order?.EmployeeId || [], [copySelectedColumns?.order?.EmployeeId])
    const getAllEmployeesParameters = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/employee/getAllEmployees', {
            attributes: ['id', 'fullname_short'],
            where: {
                id: {
                    $in: mySelectedIds
                }
            }
        })

        if (result) {
            const employeesToOrder: VerticalSortItem[] = []
            let index = 0
            for (const key in result) {
                const employee = result[key]
                const employeeOrderParameter = myEmployeesOrder?.includes(employee?.id as never) ? myEmployeesOrder?.indexOf(employee?.id as never) + 1 : null
                index = index + 1
                employeesToOrder.push({
                    name: employee.fullname_short,
                    id: employee?.id,
                    order: employeeOrderParameter ? employeeOrderParameter : index
                })
            }
            employeesToOrder.sort((a, b) => a.order - b.order)
            setMySelectedEmployees(employeesToOrder)
        }
    }, [setMySelectedEmployees, mySelectedIds, myEmployeesOrder])

    const changeData: CustomChangeFunction = useCallback((value) => {
        setCopySelectedColumns((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            if (!copy.filters.EmployeeId) {
                copy.filters.EmployeeId = { value: [] }
            }
            if (!copy.order.EmployeeId) {
                copy.order.EmployeeId = []
            }
            copy.filters.EmployeeId.value.push(parseInt(value))
            copy.order.EmployeeId.push(parseInt(value))
            return { ...copy }
        })
    }, [setCopySelectedColumns])

    const updateEmployeeConfig = useCallback(async (employee: VerticalSortItem[]) => {
        setMySelectedEmployees(employee)
        const myOrder: number[] = []
        const myIds: number[] = []
        for (const value of employee) {
            myOrder.push(parseInt(value?.id as any))
            myIds.push(parseInt(value?.id))
        }

        setCopySelectedColumns((old) => {
        const copy = JSON.parse(JSON.stringify(old))
        if (!copy.filters.EmployeeId) {
            copy.filters.EmployeeId = { value: [] }
        }
        if (!copy.order.EmployeeId) {
            copy.order.EmployeeId = []
        }
        copy.filters.EmployeeId.value = myIds
        copy.order.EmployeeId = myOrder
        return { ...copy }
    })
    }, [setCopySelectedColumns, setMySelectedEmployees])

    const updateFilterOnTable = useCallback(async (columns: SelectedColumnsAndFilters, id: number) => {
        delete columns?.id
        delete columns?.name
        const myValue = JSON.stringify(columns)
        await twinFetchPostJSON('/api/app/config/filter/updateFilter', { id, value: myValue })
    }, [])

    const updateEmployeeParams = useCallback(async (columns: SelectedColumnsAndFilters) => {
        delete columns?.id
        delete columns?.name
        const myName = 'table_' + name
        if (columns.filters.startDate) {
            delete columns.filters.startDate
        }
        if (columns.filters.endDate) {
            delete columns.filters.endDate
        }
        const myValue = JSON.stringify(columns)
        await twinFetchPostJSON('/api/app/config/employee/updateEmployeeParameter', { parameterKey: myName, value: myValue })
        changeEmployeeParams({ [myName]: myValue })
    }, [name])

    const updateParamsDecider = useCallback(async (columns: SelectedColumnsAndFilters) => {
        if (copySelectedColumns?.id && String(copySelectedColumns?.id) !== '0') {
            await updateFilterOnTable(columns, copySelectedColumns?.id)
        } else {
            await updateEmployeeParams(columns)
        }
    }, [copySelectedColumns?.id, updateFilterOnTable, updateEmployeeParams])

    const onSubmitHandler = useCallback(async () => {
        startLoading()
        const mySelectedColumns = JSON.parse(JSON.stringify(copySelectedColumns))
        await updateParamsDecider(copySelectedColumns)
        setSelectedColumnsAndFilters(mySelectedColumns)
        setOpened(null)
        return false
    }, [copySelectedColumns, setSelectedColumnsAndFilters, setOpened, updateParamsDecider, startLoading])

    const myOnChangeFilter = useCallback((json: TwinDictionary) => {
        setCopySelectedColumns({ filters: json?.value?.filters || {}, selectedColumns: json?.value?.selectedColumns || {}, id: json?.id, name: json?.name, order: json?.value?.order || {} })
    }, [setCopySelectedColumns])

    useEffect(() => {
        getAllEmployeesParameters()
    }, [getAllEmployeesParameters])

    return { loading, changeData, onSubmitHandler, mySelectedEmployees, copySelectedColumns, updateEmployeeConfig, isCustomFilter, myOnChangeFilter }
}

interface SearchBarEmployeeProps {
    className?: string
    label?: string
    excludeIds?: string[]
    changeSelectedItems: CustomChangeFunction
}

const SearchBarEmployee: React.FC<SearchBarEmployeeProps> = ({ label, excludeIds, changeSelectedItems, className}) => {
    const employees = dictionaryComplexFromJsonArr(getEmployees() || [])
    return (
        <CustomSelectWithSearchBarNotInFilteredItems excludeItems={excludeIds || []} items={employees} fieldName='fullname_short' onChange={changeSelectedItems} label={label || ''} className={className || ''} />
    )
}

export default ModalConfigStatsByEmployee